// Dates de « Nouvelle demande » : texte 'AAAA-MM-JJ', calculées sur le CALENDRIER (UTC), jamais en ajoutant des millisecondes.
// Une absence du 28 oct. au 6 nov. traverse le passage à l'heure d'hiver (25 oct. 2026) : +86 400 000 ms donnerait un jour de trop ou de moins
// selon le fuseau de la machine. Les noms de jours et de mois sont écrits ici (pas d'Intl) : même texte partout, y compris dans les tests.

export type DateIso = string;

const JOURS_COURTS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
const JOURS_LONGS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const MOIS_LONGS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const deux = (n: number) => String(n).padStart(2, '0');

function parties(s: DateIso): [number, number, number] {
  const [a, m, j] = s.slice(0, 10).split('-').map(Number);
  return [a, m, j];
}
function utc(s: DateIso): Date {
  const [a, m, j] = parties(s);
  return new Date(Date.UTC(a, m - 1, j));
}
function enIso(d: Date): DateIso {
  return `${d.getUTCFullYear()}-${deux(d.getUTCMonth() + 1)}-${deux(d.getUTCDate())}`;
}

export function addJours(s: DateIso, n: number): DateIso {
  const d = utc(s);
  d.setUTCDate(d.getUTCDate() + n);
  return enIso(d);
}
/** 1 = lundi … 7 = dimanche */
export function jourSemaine(s: DateIso): number {
  const j = utc(s).getUTCDay();
  return j === 0 ? 7 : j;
}
export const estOuvre = (s: DateIso): boolean => jourSemaine(s) <= 5;
export function lundiDe(s: DateIso): DateIso { return addJours(s, -(jourSemaine(s) - 1)); }
export function jourDuMois(s: DateIso): number { return parties(s)[2]; }
/** 'AAAA-MM' */
export const moisDe = (s: DateIso): string => s.slice(0, 7);
/** 'AAAA-MM-01' du mois de `s` */
export const premierDuMois = (s: DateIso): DateIso => `${s.slice(0, 7)}-01`;
export function dernierDuMois(s: DateIso): DateIso {
  const [a, m] = parties(s);
  return enIso(new Date(Date.UTC(a, m, 0)));
}
/** Premier du mois, `n` mois plus tard (ou plus tôt). */
export function ajouterMois(s: DateIso, n: number): DateIso {
  const [a, m] = parties(s);
  return enIso(new Date(Date.UTC(a, m - 1 + n, 1)));
}

/**
 * Une date saisie est-elle COMPLÈTE ? En tapant « 26.11.2026 » chiffre par chiffre, le champ date de Chrome donne une date valide à
 * chaque étape (année 0002, 0020, 0202…) : on ne s'en sert qu'une fois l'année entre 2000 et 2100.
 */
export function dateComplete(v: string | null | undefined): boolean {
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const [a, m, j] = parties(v);
  if (a < 2000 || a > 2100) return false;
  const d = new Date(Date.UTC(a, m - 1, j));
  return d.getUTCFullYear() === a && d.getUTCMonth() === m - 1 && d.getUTCDate() === j;
}

/** Dates de a à b inclus (ordre corrigé) ; `ouvres` ne garde que lundi→vendredi. */
export function plage(a: DateIso, b: DateIso, ouvres = true): DateIso[] {
  if (a > b) [a, b] = [b, a];
  const out: DateIso[] = [];
  for (let cur = a; cur <= b; cur = addJours(cur, 1)) if (!ouvres || estOuvre(cur)) out.push(cur);
  return out;
}

/** « mar. 10 nov. » */
export function jourCourt(s: DateIso): string {
  const d = utc(s);
  return `${JOURS_COURTS[d.getUTCDay()]} ${d.getUTCDate()} ${MOIS_COURTS[d.getUTCMonth()]}`;
}
/** « mardi 10 novembre 2026 » */
export function jourLong(s: DateIso): string {
  const d = utc(s);
  return `${JOURS_LONGS[d.getUTCDay()]} ${d.getUTCDate()} ${MOIS_LONGS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
/** « novembre 2026 » */
export function moisLong(s: DateIso): string {
  const d = utc(s);
  return `${MOIS_LONGS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
/** « nov. » (abréviation du mois, pour « 2 nov. » dans la grille) */
export function moisCourt(s: DateIso): string { return MOIS_COURTS[utc(s).getUTCMonth()]; }
/** « octobre » sans l'année quand elle est celle de `reference` */
export function moisSansAnnee(s: DateIso, reference: DateIso): string {
  const t = moisLong(s);
  return s.slice(0, 4) === reference.slice(0, 4) ? t.replace(/ \d{4}$/, '') : t;
}
